/**
 * breakup — a capital ship that dies slowly (the physics follow-up's item 7, 2026-10-05; audit-feel G11 "big things read
 * big"; audit-sim G16 "tumbling with asymmetric inertia").
 *
 * A 4.5 km fleet capital used to vanish on the frame it died, under a 2–4 s explosion. Now its hull breaks into 4–6
 * chunks that drift apart and TUMBLE for 20–40 s, venting along their cut faces, before they burn away and the hulks
 * join the wreck yard. Pure math here (no three, no clocks); SpaceModule draws it and lib/space/breakup_mesh.ts cuts the
 * geometry.
 *
 * THE SPLIT — a one-time CPU split per hull model (cached, two variants per civ), justified over clipping planes: the
 * cooked capital is ONE 18k-triangle mesh, so assigning each TRIANGLE to a chunk by which side of two or three cutting
 * planes its centroid lies costs a few milliseconds once and draws exactly the hull's triangles (clipping would draw the
 * whole hull once per chunk, with a material clone per chunk and its planes re-aimed every frame). Triangles are never
 * cut: at hull scale they are metres across, so the torn edge reads as torn. The recipe (`breakupRecipe`, hashed per
 * war cycle / side / slot): a bow cut and a stern cut across the long axis, tilted; a longitudinal cut through the
 * middle; sometimes a second cut through the bow or the stern — 4 to 6 chunks. A chunk under `MIN_TRIS` merges into its
 * nearest neighbour, so none is empty and the chunks PARTITION the triangles.
 *
 * THE TUMBLE — torque-free rigid-body motion with the chunk's own ASYMMETRIC inertia: the inertia tensor of the chunk's
 * surface (a thin shell — triangle area as mass) is diagonalised (Jacobi) into principal moments I1 ≤ I2 ≤ I3, and
 * Euler's equations are integrated in the body frame on the angular momentum L (dL/dt = L × ω, ω = I⁻¹L) together with
 * the orientation quaternion (dq/dt = ½ q ⊗ ω), RK4 at a fixed sub-step. Energy and the world angular momentum are
 * conserved, so precession and the intermediate-axis (Dzhanibekov) flip EMERGE — nothing is scripted. Spin rates scale
 * as 1/length (a kilometre-long chunk turns a few degrees a second: big things read big); linear drift is metres per
 * second away from the hull's centre on top of the hull's own drift.
 */

export type V3 = [number, number, number];
/** A quaternion as [w, x, y, z]. */
export type Q4 = [number, number, number, number];

// ─── the recipe ───────────────────────────────────────────────────────────────

export interface CutPlane { n: V3; d: number }
export interface BreakupRecipe {
  /** Bow / stern cuts across the long axis (the hull's local frame: nose −Z, centred). */
  bow: CutPlane;
  stern: CutPlane;
  /** The longitudinal cut through the middle section. */
  mid: CutPlane;
  /** An optional second cut through the bow / the stern. */
  bowSplit: CutPlane | null;
  sternSplit: CutPlane | null;
}

/** Murmur-style hash of a key into [0, 1) — the galaxy's HASH LAW (no bare FNV for geometry). */
export function h01(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const norm3 = (v: V3): V3 => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };

/**
 * The cuts for a hull of `length` (m, along local Z) and half-extents `hx` / `hy`, from `key`. Variant `v` picks one of
 * the cached splits (callers hash the war cycle / side / slot into it).
 */
export function breakupRecipe(key: string, length: number, hx: number, hy: number): BreakupRecipe {
  const r = (s: string): number => h01(`${key}|${s}`);
  const L = length;
  const tilt = (s: string, a: number): number => (r(s) - 0.5) * 2 * a;
  // bow cut: z ≈ −(0.18…0.28) L, tilted up to ~20°; stern cut: z ≈ +(0.16…0.28) L
  const bowN = norm3([tilt('bx', 0.35), tilt('by', 0.35), 1]);
  const bowZ = -(0.18 + 0.1 * r('bz')) * L;
  const sternN = norm3([tilt('sx', 0.35), tilt('sy', 0.35), 1]);
  const sternZ = (0.16 + 0.12 * r('sz')) * L;
  // the middle: a longitudinal cut through the centre, port/starboard or dorsal/ventral (hashed angle), slightly skewed
  const a = r('ma') * Math.PI;
  const midN = norm3([Math.cos(a), Math.sin(a), tilt('mz', 0.12)]);
  const mid: CutPlane = { n: midN, d: 0 };
  const bowSplit = r('bs') < 0.5 ? { n: norm3([Math.sin(a + 1.3), Math.cos(a + 1.3), tilt('bsz', 0.3)]), d: (r('bso') - 0.5) * 0.4 * Math.min(hx, hy) } : null;
  const sternSplit = r('ss') < 0.35 ? { n: norm3([Math.cos(a + 0.7), Math.sin(a + 0.7), tilt('ssz', 0.3)]), d: (r('sso') - 0.5) * 0.4 * Math.min(hx, hy) } : null;
  return { bow: { n: bowN, d: bowN[2] * bowZ }, stern: { n: sternN, d: sternN[2] * sternZ }, mid, bowSplit, sternSplit };
}

const side = (p: CutPlane, x: number, y: number, z: number): number => p.n[0] * x + p.n[1] * y + p.n[2] * z - p.d;

/** The raw chunk of a point: 0 bow, 1 bow-split, 2 stern, 3 stern-split, 4 mid A, 5 mid B. */
export function rawChunkOf(rc: BreakupRecipe, x: number, y: number, z: number): number {
  if (side(rc.bow, x, y, z) < 0) return rc.bowSplit && side(rc.bowSplit, x, y, z) > 0 ? 1 : 0;
  if (side(rc.stern, x, y, z) > 0) return rc.sternSplit && side(rc.sternSplit, x, y, z) > 0 ? 3 : 2;
  return side(rc.mid, x, y, z) < 0 ? 4 : 5;
}

/** A chunk with fewer triangles than this (or this share of the hull) merges into its nearest neighbour. */
export const MIN_TRIS = 24;
export const MIN_SHARE = 0.02;

export interface SplitResult {
  /** chunk index (0..count−1) of every triangle */
  chunkOf: Int32Array;
  count: number;
  /** per chunk: triangle count, total area, area-weighted centroid */
  tris: number[];
  area: number[];
  centroid: V3[];
}

/**
 * Assign every triangle (centroids `c`, 3 per triangle; areas `a`) to a chunk; merge the small ones; renumber densely.
 * The result PARTITIONS the triangles: every triangle in exactly one chunk, no chunk empty.
 */
export function splitTriangles(c: ArrayLike<number>, a: ArrayLike<number>, rc: BreakupRecipe): SplitResult {
  const n = a.length;
  const raw = new Int32Array(n);
  const cnt = new Array(6).fill(0), ar = new Array(6).fill(0), cx = new Array(6).fill(0), cy = new Array(6).fill(0), cz = new Array(6).fill(0);
  for (let i = 0; i < n; i++) {
    const k = rawChunkOf(rc, c[i * 3], c[i * 3 + 1], c[i * 3 + 2]);
    raw[i] = k; cnt[k]++;
    const w = Math.max(1e-9, a[i]);
    ar[k] += w; cx[k] += c[i * 3] * w; cy[k] += c[i * 3 + 1] * w; cz[k] += c[i * 3 + 2] * w;
  }
  const minT = Math.max(MIN_TRIS, Math.ceil(n * MIN_SHARE));
  const cen = (k: number): V3 => [cx[k] / ar[k], cy[k] / ar[k], cz[k] / ar[k]];
  // merge the small chunks (smallest first) into the nearest surviving chunk by centroid
  const alive = (k: number): boolean => cnt[k] >= minT;
  const map = [0, 1, 2, 3, 4, 5];
  const order = [0, 1, 2, 3, 4, 5].filter((k) => cnt[k] > 0 && !alive(k)).sort((p, q) => cnt[p] - cnt[q]);
  for (const k of order) {
    let best = -1, bd = Infinity;
    const ck = cen(k);
    for (let j = 0; j < 6; j++) {
      if (j === k || !alive(j)) continue;
      const cj = cen(j), d = Math.hypot(ck[0] - cj[0], ck[1] - cj[1], ck[2] - cj[2]);
      if (d < bd) { bd = d; best = j; }
    }
    if (best < 0) continue; // nothing survives: keep it (a tiny hull stays one chunk)
    map[k] = best;
    cnt[best] += cnt[k]; ar[best] += ar[k]; cx[best] += cx[k]; cy[best] += cy[k]; cz[best] += cz[k];
    cnt[k] = 0; ar[k] = 0; cx[k] = cy[k] = cz[k] = 0;
  }
  const dense = new Int32Array(6).fill(-1);
  let count = 0;
  for (let k = 0; k < 6; k++) if (cnt[k] > 0) dense[k] = count++;
  const chunkOf = new Int32Array(n);
  for (let i = 0; i < n; i++) { let k = raw[i]; while (map[k] !== k) k = map[k]; chunkOf[i] = dense[k]; }
  const tris: number[] = [], area: number[] = [], centroid: V3[] = [];
  for (let k = 0; k < 6; k++) if (cnt[k] > 0) { tris.push(cnt[k]); area.push(ar[k]); centroid.push(cen(k)); }
  return { chunkOf, count, tris, area, centroid };
}

// ─── inertia ──────────────────────────────────────────────────────────────────

/** A symmetric 3×3 as [xx, yy, zz, xy, xz, yz]. */
export type Sym3 = [number, number, number, number, number, number];

/**
 * The inertia tensor (about `com`) of a thin shell: point masses `a[i]` at the triangle centroids. Units: area × m².
 * I = Σ m (|r|² Id − r rᵀ).
 */
export function shellInertia(c: ArrayLike<number>, a: ArrayLike<number>, pick: (i: number) => boolean, com: V3): Sym3 {
  let xx = 0, yy = 0, zz = 0, xy = 0, xz = 0, yz = 0;
  for (let i = 0; i < a.length; i++) {
    if (!pick(i)) continue;
    const m = Math.max(0, a[i]);
    const x = c[i * 3] - com[0], y = c[i * 3 + 1] - com[1], z = c[i * 3 + 2] - com[2];
    xx += m * (y * y + z * z); yy += m * (x * x + z * z); zz += m * (x * x + y * y);
    xy -= m * x * y; xz -= m * x * z; yz -= m * y * z;
  }
  return [xx, yy, zz, xy, xz, yz];
}

/**
 * Principal moments (ascending) and axes of a symmetric 3×3 by cyclic Jacobi. `axes` are the COLUMNS of a proper
 * rotation (det +1) taking principal (body) coordinates to the input frame: v_in = A · v_body.
 */
export function principal(s: Sym3): { I: V3; A: [V3, V3, V3] } {
  const m = [[s[0], s[3], s[4]], [s[3], s[1], s[5]], [s[4], s[5], s[2]]];
  const v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let sweep = 0; sweep < 32; sweep++) {
    const off = Math.abs(m[0][1]) + Math.abs(m[0][2]) + Math.abs(m[1][2]);
    if (off < 1e-14 * (Math.abs(m[0][0]) + Math.abs(m[1][1]) + Math.abs(m[2][2]) + 1e-30)) break;
    for (const [p, q] of [[0, 1], [0, 2], [1, 2]] as const) {
      if (Math.abs(m[p][q]) < 1e-300) continue;
      const th = (m[q][q] - m[p][p]) / (2 * m[p][q]);
      const t = Math.sign(th || 1) / (Math.abs(th) + Math.sqrt(th * th + 1));
      const cs = 1 / Math.sqrt(t * t + 1), sn = t * cs;
      for (let k = 0; k < 3; k++) { const a = m[k][p], b = m[k][q]; m[k][p] = cs * a - sn * b; m[k][q] = sn * a + cs * b; }
      for (let k = 0; k < 3; k++) { const a = m[p][k], b = m[q][k]; m[p][k] = cs * a - sn * b; m[q][k] = sn * a + cs * b; }
      for (let k = 0; k < 3; k++) { const a = v[k][p], b = v[k][q]; v[k][p] = cs * a - sn * b; v[k][q] = sn * a + cs * b; }
    }
  }
  const idx = [0, 1, 2].sort((i, j) => m[i][i] - m[j][j]);
  const I: V3 = [m[idx[0]][idx[0]], m[idx[1]][idx[1]], m[idx[2]][idx[2]]];
  const col = (j: number): V3 => [v[0][j], v[1][j], v[2][j]];
  const a0 = col(idx[0]), a1 = col(idx[1]);
  // a2 = a0 × a1: a right-handed frame whatever Jacobi's column signs were
  const a2: V3 = [a0[1] * a1[2] - a0[2] * a1[1], a0[2] * a1[0] - a0[0] * a1[2], a0[0] * a1[1] - a0[1] * a1[0]];
  return { I, A: [a0, a1, a2] };
}

/** The rotation matrix with columns `A` as a quaternion [w, x, y, z]. */
export function quatFromAxes(A: [V3, V3, V3]): Q4 {
  const m00 = A[0][0], m01 = A[1][0], m02 = A[2][0];
  const m10 = A[0][1], m11 = A[1][1], m12 = A[2][1];
  const m20 = A[0][2], m21 = A[1][2], m22 = A[2][2];
  const tr = m00 + m11 + m22;
  let w: number, x: number, y: number, z: number;
  if (tr > 0) { const s = Math.sqrt(tr + 1) * 2; w = 0.25 * s; x = (m21 - m12) / s; y = (m02 - m20) / s; z = (m10 - m01) / s; }
  else if (m00 > m11 && m00 > m22) { const s = Math.sqrt(1 + m00 - m11 - m22) * 2; w = (m21 - m12) / s; x = 0.25 * s; y = (m01 + m10) / s; z = (m02 + m20) / s; }
  else if (m11 > m22) { const s = Math.sqrt(1 + m11 - m00 - m22) * 2; w = (m02 - m20) / s; x = (m01 + m10) / s; y = 0.25 * s; z = (m12 + m21) / s; }
  else { const s = Math.sqrt(1 + m22 - m00 - m11) * 2; w = (m10 - m01) / s; x = (m02 + m20) / s; y = (m12 + m21) / s; z = 0.25 * s; }
  return qnorm([w, x, y, z]);
}

// ─── the tumble ───────────────────────────────────────────────────────────────

export interface Tumble {
  /** body → world orientation */
  q: Q4;
  /** angular momentum in the BODY (principal) frame */
  L: V3;
  /** principal moments */
  I: V3;
}

export const TUMBLE = {
  /** The integrator's fixed sub-step (s): RK4 at this keeps energy to ~1e-9 over a minute at chunk spin rates. */
  stepS: 1 / 120,
  /** The most sub-steps one call takes (a stalled frame advances at most this much — a few seconds). */
  maxSteps: 1200,
} as const;

export const qnorm = (q: Q4): Q4 => { const l = Math.hypot(q[0], q[1], q[2], q[3]) || 1; return [q[0] / l, q[1] / l, q[2] / l, q[3] / l]; };
/** q ⊗ (0, w) */
const qmulPure = (q: Q4, w: V3): Q4 => [
  -q[1] * w[0] - q[2] * w[1] - q[3] * w[2],
  q[0] * w[0] + q[2] * w[2] - q[3] * w[1],
  q[0] * w[1] - q[1] * w[2] + q[3] * w[0],
  q[0] * w[2] + q[1] * w[1] - q[2] * w[0],
];
/** Rotate a body-frame vector into the world by q. */
export function qrot(q: Q4, v: V3): V3 {
  const [w, x, y, z] = q;
  const tx = 2 * (y * v[2] - z * v[1]), ty = 2 * (z * v[0] - x * v[2]), tz = 2 * (x * v[1] - y * v[0]);
  return [v[0] + w * tx + (y * tz - z * ty), v[1] + w * ty + (z * tx - x * tz), v[2] + w * tz + (x * ty - y * tx)];
}

type St = { q: Q4; L: V3 };
function deriv(s: St, I: V3): St {
  const w: V3 = [s.L[0] / I[0], s.L[1] / I[1], s.L[2] / I[2]];
  // torque-free in the body frame: dL/dt = L × ω
  const dL: V3 = [s.L[1] * w[2] - s.L[2] * w[1], s.L[2] * w[0] - s.L[0] * w[2], s.L[0] * w[1] - s.L[1] * w[0]];
  const dq = qmulPure(s.q, w);
  return { q: [dq[0] * 0.5, dq[1] * 0.5, dq[2] * 0.5, dq[3] * 0.5], L: dL };
}
const addSt = (a: St, b: St, h: number): St => ({
  q: [a.q[0] + b.q[0] * h, a.q[1] + b.q[1] * h, a.q[2] + b.q[2] * h, a.q[3] + b.q[3] * h],
  L: [a.L[0] + b.L[0] * h, a.L[1] + b.L[1] * h, a.L[2] + b.L[2] * h],
});

/** One RK4 step of `h` seconds. */
export function rk4Tumble(b: Tumble, h: number): void {
  const s0: St = { q: b.q, L: b.L };
  const k1 = deriv(s0, b.I);
  const k2 = deriv(addSt(s0, k1, h / 2), b.I);
  const k3 = deriv(addSt(s0, k2, h / 2), b.I);
  const k4 = deriv(addSt(s0, k3, h), b.I);
  const q: Q4 = [0, 0, 0, 0], L: V3 = [0, 0, 0];
  for (let i = 0; i < 4; i++) q[i] = b.q[i] + (h / 6) * (k1.q[i] + 2 * k2.q[i] + 2 * k3.q[i] + k4.q[i]);
  for (let i = 0; i < 3; i++) L[i] = b.L[i] + (h / 6) * (k1.L[i] + 2 * k2.L[i] + 2 * k3.L[i] + k4.L[i]);
  b.q = qnorm(q);
  b.L = L;
}

/** Advance `dt` seconds in fixed sub-steps (the last one shortened). */
export function stepTumble(b: Tumble, dt: number): void {
  if (!(dt > 0)) return;
  let left = Math.min(dt, TUMBLE.stepS * TUMBLE.maxSteps);
  while (left > 1e-12) { const h = Math.min(TUMBLE.stepS, left); rk4Tumble(b, h); left -= h; }
}

/** Rotational kinetic energy ½ Σ L²/I. */
export const tumbleEnergy = (b: Tumble): number => 0.5 * (b.L[0] ** 2 / b.I[0] + b.L[1] ** 2 / b.I[1] + b.L[2] ** 2 / b.I[2]);
/** The world-frame angular momentum (conserved, torque-free). */
export const worldMomentum = (b: Tumble): V3 => qrot(b.q, b.L);
/** The body-frame angular velocity. */
export const omegaBody = (b: Tumble): V3 => [b.L[0] / b.I[0], b.L[1] / b.I[1], b.L[2] / b.I[2]];

/**
 * A chunk's initial spin: a body-frame angular velocity of magnitude `rate` (rad/s) about a hashed direction — biased
 * toward the intermediate axis for some chunks (so the flip can show) — turned into L = I ω.
 */
export function initialSpin(key: string, I: V3, rate: number): V3 {
  const r = (s: string): number => h01(`${key}|${s}`);
  let w: V3;
  if (r('mid') < 0.4) {
    // near the intermediate axis, a few percent off it: the Dzhanibekov flip emerges
    w = norm3([(r('a') - 0.5) * 0.12, 1, (r('c') - 0.5) * 0.12]);
  } else {
    const th = r('t') * Math.PI * 2, ph = Math.acos(2 * r('p') - 1);
    w = [Math.sin(ph) * Math.cos(th), Math.cos(ph), Math.sin(ph) * Math.sin(th)];
  }
  return [I[0] * w[0] * rate, I[1] * w[1] * rate, I[2] * w[2] * rate];
}

// ─── the life of a chunk ──────────────────────────────────────────────────────

export const BREAKUP = {
  /** A chunk lives this long (s) before it has burned away — hashed per chunk inside the band. */
  lifeS: [20, 40] as const,
  /** It burns away over its last seconds (the dissolve). */
  fadeS: 5,
  /** Spin rate (rad/s) = spinK / chunk length (m), × a hashed 0.8…1.5: a 1.5 km chunk turns ~1.5°/s. */
  spinK: 40,
  /** Drift (m/s) away from the hull's centre, hashed inside the band. */
  driftMps: [5, 14] as const,
  /** Vents per second per chunk at the start, decaying to `ventEnd` at the end of its life. */
  ventStart: 9,
  ventEnd: 0.8,
  /** Secondary blasts along the cuts in the first seconds. */
  blastS: 9,
  /** Cut-face samples kept per chunk (the vents' seats). */
  ventSeats: 12,
  /** A triangle within this share of the hull length of a cutting plane is on the cut face. */
  cutBand: 0.03,
  /** At most this many breakups at once (the oldest ends early). */
  maxLive: 6,
} as const;

/** A chunk's life (s), from its key. */
export const chunkLife = (key: string): number => BREAKUP.lifeS[0] + (BREAKUP.lifeS[1] - BREAKUP.lifeS[0]) * h01(`${key}|life`);
/** The vents' rate at age `t` of a chunk living `life` s. */
export const ventRate = (t: number, life: number): number => {
  const u = Math.min(1, Math.max(0, t / Math.max(1e-3, life)));
  return BREAKUP.ventStart + (BREAKUP.ventEnd - BREAKUP.ventStart) * u;
};
/** The dissolve's progress at age `t` (0 until the last `fadeS` seconds, then to 1). */
export const fadeProgress = (t: number, life: number): number => Math.min(1, Math.max(0, (t - (life - BREAKUP.fadeS)) / BREAKUP.fadeS));

/** The planes that bound a raw chunk (`rawChunkOf`): the faces it was cut along. */
export function cutPlanesOf(rc: BreakupRecipe, raw: number): CutPlane[] {
  const out: CutPlane[] = [];
  if (raw <= 1) { out.push(rc.bow); if (rc.bowSplit) out.push(rc.bowSplit); }
  else if (raw <= 3) { out.push(rc.stern); if (rc.sternSplit) out.push(rc.sternSplit); }
  else out.push(rc.bow, rc.stern, rc.mid);
  return out;
}

/** The plane of a raw chunk's cut face a triangle centroid lies on (within `band` metres), else null. */
export function onCut(rc: BreakupRecipe, raw: number, x: number, y: number, z: number, band: number): CutPlane | null {
  for (const p of cutPlanesOf(rc, raw)) if (Math.abs(side(p, x, y, z)) < band) return p;
  return null;
}
